import React, { useEffect, useMemo, useState } from "react";
import { Check } from "lucide-react";
import { Button } from "../ui/Button";
import { useToast } from "../../hooks/use-toast";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../ui/Dialog";
import { updatePatientField } from "./patientApi";
import { StitchedTranscriptPanel, useSortedTranscriptionJobs } from "./TranscriptionDisplay";
import ChartHistoryTable from "./ChartHistoryTable";
import SummarizationJobPicker from "./SummarizationJobPicker";
import NoteDiffView from "./NoteDiffView";
import { useSummarizationJobSelection } from "./useSummarizationJobSelection";
import { useSummarizeJobTemplates } from "./useSummarizeJobTemplates";
import SummarizeTemplatesPanel from "./SummarizeTemplatesPanel";
import {
  CHART_COLUMN_TAB,
  getChartColumnTabs,
} from "./smartChartUtils";
import { transcriptNoteModalColumnText } from "./supportStyles";

const NoteEditor = ({ patientId, note, onPatientUpdate, readOnly = false }) => {
  const { toast } = useToast();
  const [draft, setDraft] = useState(note ?? "");
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const [savedRecently, setSavedRecently] = useState(false);

  useEffect(() => {
    setDraft(note ?? "");
    setMessage(null);
    setIsSaving(false);
    setSavedRecently(false);
  }, [patientId, note]);

  const savedNote = note ?? "";
  const isDirty = draft !== savedNote;

  const handleSave = async () => {
    if (!patientId || !isDirty) return;

    setIsSaving(true);
    setMessage(null);

    const result = await updatePatientField(patientId, "note", draft);

    setIsSaving(false);

    if (result.success) {
      onPatientUpdate?.({ note: draft });
      setMessage(null);
      setSavedRecently(true);
      setTimeout(() => setSavedRecently(false), 3000);
      toast({
        title: "Note saved",
        description: "The clinical note was saved to the database.",
      });
      return;
    }

    const errorText = result.error || "Failed to save note.";
    setMessage({ type: "error", text: errorText });
    toast({
      variant: "destructive",
      title: "Could not save note",
      description: errorText,
    });
  };

  return (
    <div className="flex flex-col flex-1 min-h-0 h-full">
      <textarea
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        disabled={isSaving || readOnly}
        readOnly={readOnly}
        placeholder="Clinical note…"
        className={`flex-1 w-full min-h-0 px-3 py-2 border !text-base !leading-relaxed rounded-md text-gray-800 bg-white placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed resize-none overflow-auto transition-shadow ${
          savedRecently
            ? "border-green-400 ring-2 ring-green-200 focus:ring-green-300"
            : "border-gray-300 focus:ring-ktx-purple"
        }`}
      />
      <div className="mt-3 flex items-center justify-between gap-3 shrink-0">
        {savedRecently ? (
          <p
            className="inline-flex items-center gap-1.5 text-sm font-medium text-green-700"
            role="status"
          >
            <Check className="h-4 w-4 shrink-0" aria-hidden />
            Saved to database
          </p>
        ) : message ? (
          <p className="text-sm text-red-600" role="alert">
            {message.text}
          </p>
        ) : (
          <span className="text-xs text-gray-400">
            {isDirty ? "Unsaved changes" : ""}
          </span>
        )}
        <Button
          type="button"
          onClick={handleSave}
          disabled={isSaving || !isDirty || readOnly}
          className={`shrink-0 text-white ${
            savedRecently
              ? "bg-green-600 hover:bg-green-600"
              : "bg-ktx-purple hover:bg-purple-700"
          }`}
        >
          {isSaving ? (
            "Saving…"
          ) : savedRecently ? (
            <span className="inline-flex items-center gap-1.5">
              <Check className="h-4 w-4" aria-hidden />
              Saved
            </span>
          ) : (
            "Save note"
          )}
        </Button>
      </div>
    </div>
  );
};

const panelClass =
  "flex flex-col min-h-0 h-full rounded-md border border-gray-200 bg-white overflow-hidden";

const panelHeaderClass =
  "shrink-0 px-3 py-2 border-b border-gray-200 bg-slate-50 text-sm font-semibold text-gray-900";

const scrollPanelBodyClass = "flex-1 min-h-0 p-3 overflow-y-auto";

const notePanelBodyClass =
  "flex flex-col flex-1 min-h-0 p-3 overflow-hidden";

const PanelTabToggle = ({ tabs, activeTab, onChange, ariaLabel }) => (
  <div
    className="flex flex-wrap rounded-md border border-gray-300 overflow-hidden text-xs font-medium shrink-0 max-w-full"
    role="tablist"
    aria-label={ariaLabel}
  >
    {tabs.map(({ id, label }) => {
      const isActive = activeTab === id;
      return (
        <button
          key={id}
          type="button"
          role="tab"
          aria-selected={isActive}
          onClick={() => onChange(id)}
          className={`px-2.5 py-1 transition-colors whitespace-nowrap ${
            isActive
              ? "bg-ktx-purple text-white"
              : "bg-white text-gray-600 hover:bg-slate-100"
          }`}
        >
          {label}
        </button>
      );
    })}
  </div>
);

const NoteModeToggle = ({ mode, onChange }) => (
  <PanelTabToggle
    tabs={[
      { id: "edit", label: "Edit" },
      { id: "compare", label: "Compare" },
    ]}
    activeTab={mode}
    onChange={onChange}
    ariaLabel="Final note mode"
  />
);

const SmartChartTextPanel = ({ content }) => (
  <pre
    className={`${transcriptNoteModalColumnText} text-sm bg-gray-50 border border-gray-200 rounded p-3 overflow-auto whitespace-pre-wrap text-gray-800 min-h-[8rem]`}
  >
    {content}
  </pre>
);

const TranscriptAndNoteModal = ({
  open,
  onOpenChange,
  patient,
  smartChart,
  smartChartLoading,
  smartChartError,
  transcription,
  transcriptionLoading,
  transcriptionError,
  summarization,
  summarizationLoading,
  summarizationError,
  onPatientUpdate,
}) => {
  const sortedJobs = useSortedTranscriptionJobs(transcription);

  const {
    sortedJobs: summarizeJobs,
    selectedJobName,
    selectJob,
    clearSelection,
    aiNote,
    aiNoteLoading,
    aiNoteError,
  } = useSummarizationJobSelection(patient?.patient_id, summarization);

  const summarizeJobTemplates = useSummarizeJobTemplates(
    summarization,
    open,
    summarizationLoading
  );

  const [noteMode, setNoteMode] = useState("edit");
  const [chartColumnTab, setChartColumnTab] = useState(
    CHART_COLUMN_TAB.CHART_HISTORY
  );

  const chartColumnTabs = useMemo(
    () => getChartColumnTabs(smartChart),
    [smartChart]
  );

  useEffect(() => {
    if (!open) {
      setNoteMode("edit");
      setChartColumnTab(CHART_COLUMN_TAB.CHART_HISTORY);
      clearSelection();
    }
  }, [open, clearSelection]);

  useEffect(() => {
    if (!chartColumnTabs.some((tab) => tab.id === chartColumnTab)) {
      setChartColumnTab(CHART_COLUMN_TAB.CHART_HISTORY);
    }
  }, [chartColumnTab, chartColumnTabs]);

  useEffect(() => {
    if (noteMode === "edit") {
      clearSelection();
    }
  }, [noteMode, clearSelection]);

  const isCompareMode = noteMode === "compare";
  const showChartColumnTabs = chartColumnTabs.length > 1;

  const renderChartColumnContent = () => {
    if (chartColumnTab === CHART_COLUMN_TAB.TEMPLATES) {
      return (
        <SummarizeTemplatesPanel
          loading={summarizeJobTemplates.loading}
          error={summarizeJobTemplates.error || summarizationError}
          sourceJobName={summarizeJobTemplates.sourceJobName}
          convPromptName={summarizeJobTemplates.convPromptName}
          startingNoteName={summarizeJobTemplates.startingNoteName}
          convPromptTemplate={summarizeJobTemplates.convPromptTemplate}
          startingNoteTemplate={summarizeJobTemplates.startingNoteTemplate}
        />
      );
    }

    if (chartColumnTab === CHART_COLUMN_TAB.ADDITIONAL_CONTEXT) {
      if (smartChartLoading) {
        return (
          <p className={`${transcriptNoteModalColumnText} text-gray-500`}>
            Loading additional context…
          </p>
        );
      }
      if (smartChartError) {
        return (
          <p className={`${transcriptNoteModalColumnText} text-red-600`}>
            {smartChartError}
          </p>
        );
      }
      return <SmartChartTextPanel content={smartChart?.starting_note ?? ""} />;
    }

    if (chartColumnTab === CHART_COLUMN_TAB.CUSTOM_PROMPT) {
      if (smartChartLoading) {
        return (
          <p className={`${transcriptNoteModalColumnText} text-gray-500`}>
            Loading custom prompt…
          </p>
        );
      }
      if (smartChartError) {
        return (
          <p className={`${transcriptNoteModalColumnText} text-red-600`}>
            {smartChartError}
          </p>
        );
      }
      return <SmartChartTextPanel content={smartChart?.custom_prompt ?? ""} />;
    }

    return <ChartHistoryTable chartHistory={patient?.chart_history} />;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[96rem] w-[95vw] max-h-[92vh] h-[92vh] flex flex-col overflow-hidden gap-0">
        <DialogHeader className="shrink-0 pb-2">
          <DialogTitle>Review Note</DialogTitle>
        </DialogHeader>

        <div className="flex-1 min-h-0 overflow-y-auto lg:overflow-hidden">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 h-full min-h-0 lg:grid-rows-1">
            <section className={panelClass}>
              <div
                className={`${panelHeaderClass} flex items-center justify-between gap-2`}
              >
                <h3 className="text-sm font-semibold text-gray-900 shrink-0">
                  {showChartColumnTabs ? "Chart" : "Chart history"}
                </h3>
                {showChartColumnTabs && (
                  <PanelTabToggle
                    tabs={chartColumnTabs}
                    activeTab={chartColumnTab}
                    onChange={setChartColumnTab}
                    ariaLabel="Chart column"
                  />
                )}
              </div>
              <div className={scrollPanelBodyClass}>{renderChartColumnContent()}</div>
            </section>

            <section className={panelClass}>
              <h3 className={panelHeaderClass}>Transcript</h3>
              <div className={scrollPanelBodyClass}>
                {transcriptionLoading && (
                  <p className={`${transcriptNoteModalColumnText} text-gray-500`}>
                    Loading transcription data…
                  </p>
                )}
                {!transcriptionLoading && transcriptionError && (
                  <p className={`${transcriptNoteModalColumnText} text-red-600`}>
                    {transcriptionError}
                  </p>
                )}
                {!transcriptionLoading && !transcriptionError && (
                  <StitchedTranscriptPanel
                    sortedJobs={sortedJobs}
                    embedded
                    loadAudio={open}
                    bodyTextClass={transcriptNoteModalColumnText}
                  />
                )}
              </div>
            </section>

            <section className={panelClass}>
              <div
                className={`${panelHeaderClass} flex items-center justify-between gap-2`}
              >
                <h3 className="text-sm font-semibold text-gray-900">Final note</h3>
                <NoteModeToggle mode={noteMode} onChange={setNoteMode} />
              </div>
              <div className={notePanelBodyClass}>
                {isCompareMode ? (
                  <>
                    <SummarizationJobPicker
                      sortedJobs={summarizeJobs}
                      selectedJobName={selectedJobName}
                      onSelectJob={selectJob}
                      summarizationLoading={summarizationLoading}
                      summarizationError={summarizationError}
                    />

                    <div className="flex flex-col flex-1 min-h-0 mt-2">
                      {!selectedJobName ? (
                        <p className={`${transcriptNoteModalColumnText} text-gray-500`}>
                          Select a summarization job to compare with the final note.
                        </p>
                      ) : aiNoteLoading ? (
                        <p className={`${transcriptNoteModalColumnText} text-gray-500`}>
                          Loading comparison…
                        </p>
                      ) : aiNoteError && !aiNote ? (
                        <p className={`${transcriptNoteModalColumnText} text-amber-700`}>
                          {aiNoteError}
                        </p>
                      ) : (
                        <NoteDiffView
                          aiNote={aiNote}
                          finalNote={patient?.note ?? ""}
                        />
                      )}
                    </div>
                  </>
                ) : (
                  <NoteEditor
                    patientId={patient?.patient_id}
                    note={patient?.note}
                    onPatientUpdate={onPatientUpdate}
                  />
                )}
              </div>
            </section>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default TranscriptAndNoteModal;
